import {
  decodeBinaryFrame,
  encodeBinaryFrame,
  type ApiErrorBody,
  type Channel,
  type ClientFrame,
  type EventFrame,
  type ServerFrame,
} from "@agentsaas/protocol";
import { ClientApiError } from "./rest.js";

/**
 * 实时 WS 客户端（doc/api_v1.md §2）：一条连接多路订阅。
 * - 记录每个通道最后收到的 seq，重连时 `sub{since}` 补发；服务端回 `gap` 时回调调用方走 REST 全量刷新
 * - 断线指数退避重连（带抖动），页面回到前台 / 网络恢复时立即重连
 * - 心跳：前台 25s、后台 60s；超过 2 个周期收不到任何帧视为断线
 * - 终端输出走二进制帧 `[1 字节通道号][payload]`，通道号在 `subscribed` 里下发
 */

/** 与浏览器 WebSocket 兼容的最小接口（便于测试和原生壳注入） */
export interface WsLike {
  readonly readyState: number;
  binaryType: string;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  send(data: string | ArrayBufferLike | Uint8Array): void;
  close(code?: number, reason?: string): void;
}
export type WsFactory = (url: string) => WsLike;

export interface VisibilitySource {
  isHidden(): boolean;
  onChange(cb: () => void): () => void;
}

export type ConnectionState = "idle" | "connecting" | "open" | "reconnecting" | "closed";

export interface WsClientOptions {
  /** 完整 ws(s) URL；可以是异步函数（例如先换一次性 ticket） */
  url: string | (() => string | Promise<string>);
  createSocket?: WsFactory;
  heartbeatMs?: number;
  hiddenHeartbeatMs?: number;
  backoff?: { initialMs?: number; maxMs?: number; factor?: number };
  /** 0..1 的随机数；测试时可固定 */
  random?: () => number;
  visibility?: VisibilitySource;
  /** 网络恢复事件（浏览器 online） */
  onlineSource?: { onChange(cb: () => void): () => void };
}

export interface Subscriber {
  onEvent?(evt: EventFrame): void;
  /** 补发窗口已过期：调用方应走 REST 全量刷新 */
  onGap?(): void;
  onSubscribed?(info: { seq: number; binaryChannel?: number }): void;
  /** 终端二进制输出 */
  onBinary?(payload: Uint8Array): void;
}

interface ChannelState {
  subs: Set<Subscriber>;
  lastSeq?: number;
  binaryChannel?: number;
}

const OPEN = 1;

export class WsClient {
  private socket: WsLike | null = null;
  private channels = new Map<Channel, ChannelState>();
  /** 退订后仍记住各通道的 seq：再次订阅时带 since，补上离开期间的事件（超窗口由服务端回 gap） */
  private seqMemory = new Map<Channel, number>();
  private binaryToChannel = new Map<number, Channel>();
  private pending = new Map<string, { resolve(v: unknown): void; reject(e: unknown): void }>();
  private stateListeners = new Set<(s: ConnectionState) => void>();
  private _state: ConnectionState = "idle";
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFrameAt = 0;
  private cmdSeq = 0;
  private disposers: (() => void)[] = [];
  /** 连接建立前排队的 cmd */
  private outbox: ClientFrame[] = [];
  private readonly opts: Required<Pick<WsClientOptions, "heartbeatMs" | "hiddenHeartbeatMs" | "random">> & WsClientOptions;

  constructor(opts: WsClientOptions) {
    this.opts = { heartbeatMs: 25_000, hiddenHeartbeatMs: 60_000, random: Math.random, ...opts };
    const vis = opts.visibility ?? browserVisibility();
    if (vis) {
      this.opts.visibility = vis;
      this.disposers.push(
        vis.onChange(() => {
          if (!vis.isHidden() && this._state === "reconnecting") this.reconnectNow();
          else this.scheduleHeartbeat();
        }),
      );
    }
    const online = opts.onlineSource ?? browserOnline();
    if (online) this.disposers.push(online.onChange(() => this._state === "reconnecting" && this.reconnectNow()));
  }

  get state(): ConnectionState {
    return this._state;
  }

  onStateChange(cb: (s: ConnectionState) => void): () => void {
    this.stateListeners.add(cb);
    return () => this.stateListeners.delete(cb);
  }

  /** 建立连接（subscribe 时也会自动调用） */
  connect(): void {
    if (this._state === "open" || this._state === "connecting" || this._state === "closed") return;
    void this.open();
  }

  /** 订阅一个通道；返回取消订阅函数。同一通道多个订阅者共享一次服务端订阅。 */
  subscribe(ch: Channel, sub: Subscriber): () => void {
    let st = this.channels.get(ch);
    const isNew = !st;
    if (!st) {
      st = { subs: new Set(), lastSeq: this.seqMemory.get(ch) };
      this.channels.set(ch, st);
    }
    st.subs.add(sub);
    if (isNew && this.socket?.readyState === OPEN) this.sendSub(ch, st);
    this.connect();
    return () => {
      const cur = this.channels.get(ch);
      if (!cur) return;
      cur.subs.delete(sub);
      if (cur.subs.size === 0) {
        this.channels.delete(ch);
        if (cur.lastSeq !== undefined) this.seqMemory.set(ch, cur.lastSeq);
        else this.seqMemory.delete(ch);
        if (cur.binaryChannel !== undefined) this.binaryToChannel.delete(cur.binaryChannel);
        if (this.socket?.readyState === OPEN) this.send({ t: "unsub", ch });
      }
    };
  }

  lastSeq(ch: Channel): number | undefined {
    return this.channels.get(ch)?.lastSeq;
  }

  binaryChannelOf(ch: Channel): number | undefined {
    return this.channels.get(ch)?.binaryChannel;
  }

  /** 发送 cmd（终端输入、中断等），等待 ack / nack；连接建立中时先排队，连上后按序发送 */
  cmd(ch: Channel, op: string, data: unknown = {}): Promise<unknown> {
    const id = `c${++this.cmdSeq}`;
    return new Promise((resolve, reject) => {
      const frame: ClientFrame = { t: "cmd", id, ch, op, data };
      this.pending.set(id, { resolve, reject });
      if (this.socket?.readyState === OPEN) this.send(frame);
      else if (this._state === "connecting" || this._state === "reconnecting" || this._state === "idle") {
        this.outbox.push(frame);
        this.connect();
      } else {
        this.pending.delete(id);
        reject(new ClientApiError("common.unavailable"));
      }
    });
  }

  /** 发送原始二进制帧（通道需已订阅且拿到通道号） */
  sendBinary(ch: Channel, payload: Uint8Array): boolean {
    const bc = this.binaryChannelOf(ch);
    if (bc === undefined || this.socket?.readyState !== OPEN) return false;
    this.socket.send(encodeBinaryFrame(bc, payload));
    return true;
  }

  close(): void {
    this.setState("closed");
    this.clearTimers();
    for (const d of this.disposers) d();
    this.disposers = [];
    this.failPending();
    const s = this.socket;
    this.socket = null;
    s?.close(1000, "client closed");
  }

  // ───────────── 内部 ─────────────

  private setState(s: ConnectionState) {
    if (this._state === s) return;
    this._state = s;
    for (const l of this.stateListeners) l(s);
  }

  private async open() {
    this.setState(this.attempt === 0 ? "connecting" : "reconnecting");
    let url: string;
    try {
      url = typeof this.opts.url === "function" ? await this.opts.url() : this.opts.url;
    } catch {
      this.scheduleReconnect();
      return;
    }
    if (this._state === "closed") return;
    const factory = this.opts.createSocket ?? ((u: string) => new WebSocket(u) as unknown as WsLike);
    const sock = factory(url);
    sock.binaryType = "arraybuffer";
    this.socket = sock;
    sock.onopen = () => {
      if (this.socket !== sock) return;
      this.attempt = 0;
      this.lastFrameAt = Date.now();
      this.setState("open");
      for (const [ch, st] of this.channels) this.sendSub(ch, st);
      for (const f of this.outbox.splice(0)) this.send(f);
      this.scheduleHeartbeat();
    };
    sock.onmessage = (ev) => {
      if (this.socket !== sock) return;
      this.lastFrameAt = Date.now();
      this.handleMessage(ev.data);
    };
    sock.onclose = () => {
      if (this.socket !== sock) return;
      this.socket = null;
      this.failPending();
      if (this._state !== "closed") this.scheduleReconnect();
    };
    sock.onerror = () => {
      /* onclose 随后触发 */
    };
  }

  private sendSub(ch: Channel, st: ChannelState) {
    this.send(st.lastSeq !== undefined ? { t: "sub", ch, since: st.lastSeq } : { t: "sub", ch });
  }

  private send(f: ClientFrame) {
    this.socket?.send(JSON.stringify(f));
  }

  private handleMessage(data: unknown) {
    if (typeof data !== "string") {
      const bytes = data instanceof Uint8Array ? data : data instanceof ArrayBuffer ? new Uint8Array(data) : null;
      if (!bytes) return;
      const { channel, payload } = decodeBinaryFrame(bytes);
      const ch = this.binaryToChannel.get(channel);
      const st = ch && this.channels.get(ch);
      if (st) for (const s of st.subs) s.onBinary?.(payload);
      return;
    }
    let f: ServerFrame;
    try {
      f = JSON.parse(data) as ServerFrame;
    } catch {
      return;
    }
    switch (f.t) {
      case "evt": {
        const st = this.channels.get(f.ch);
        if (!st) return;
        // 补发与实时流可能重叠：按 seq 去重
        if (st.lastSeq !== undefined && f.seq <= st.lastSeq) return;
        st.lastSeq = f.seq;
        for (const s of st.subs) s.onEvent?.(f);
        return;
      }
      case "subscribed": {
        const st = this.channels.get(f.ch);
        if (!st) return;
        if (st.lastSeq === undefined) st.lastSeq = f.seq;
        if (f.binaryChannel !== undefined) {
          st.binaryChannel = f.binaryChannel;
          this.binaryToChannel.set(f.binaryChannel, f.ch);
        }
        for (const s of st.subs) s.onSubscribed?.({ seq: f.seq, binaryChannel: f.binaryChannel });
        return;
      }
      case "gap": {
        const st = this.channels.get(f.ch);
        if (!st) return;
        // 从当前位置重新开始计数；调用方全量刷新
        st.lastSeq = undefined;
        for (const s of st.subs) s.onGap?.();
        return;
      }
      case "ack": {
        const p = this.pending.get(f.id);
        this.pending.delete(f.id);
        p?.resolve(f.data);
        return;
      }
      case "nack": {
        const p = this.pending.get(f.id);
        this.pending.delete(f.id);
        const e: ApiErrorBody = f.error;
        p?.reject(new ClientApiError(e.code, e.params, e.fields, e.requestId));
        return;
      }
      case "pong":
        return;
    }
  }

  /** 当前心跳间隔：后台时降低频率 */
  heartbeatInterval(): number {
    return this.opts.visibility?.isHidden() ? this.opts.hiddenHeartbeatMs : this.opts.heartbeatMs;
  }

  private scheduleHeartbeat() {
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    if (this._state !== "open") return;
    const interval = this.heartbeatInterval();
    this.heartbeatTimer = setTimeout(() => {
      if (this._state !== "open" || !this.socket) return;
      if (Date.now() - this.lastFrameAt > interval * 2) {
        // 半开连接：主动断开，走重连
        const s = this.socket;
        this.socket = null;
        s.close(4000, "heartbeat timeout");
        this.failPending();
        this.scheduleReconnect();
        return;
      }
      this.send({ t: "ping" });
      this.scheduleHeartbeat();
    }, interval);
  }

  /** 第 n 次重连的等待时间（指数退避 + 50%~100% 抖动） */
  backoffDelay(n: number): number {
    const { initialMs = 500, maxMs = 30_000, factor = 2 } = this.opts.backoff ?? {};
    const raw = Math.min(maxMs, initialMs * factor ** n);
    return Math.round(raw * (0.5 + this.opts.random() / 2));
  }

  private scheduleReconnect() {
    if (this._state === "closed") return;
    this.clearTimers();
    this.setState("reconnecting");
    const delay = this.backoffDelay(this.attempt++);
    this.reconnectTimer = setTimeout(() => void this.open(), delay);
  }

  private reconnectNow() {
    this.clearTimers();
    void this.open();
  }

  private clearTimers() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    this.reconnectTimer = this.heartbeatTimer = null;
  }

  /** 已发出但未确认的 cmd 在断线时失败（无法确认是否执行）；排队中的保留到重连后发送 */
  private failPending() {
    const queued = new Set(this.outbox.map((f) => (f.t === "cmd" ? f.id : "")));
    for (const [id, p] of this.pending) {
      if (queued.has(id) && this._state !== "closed") continue;
      p.reject(new ClientApiError("common.unavailable"));
      this.pending.delete(id);
    }
    if (this._state === "closed") this.outbox = [];
  }
}

export function createWsClient(opts: WsClientOptions): WsClient {
  return new WsClient(opts);
}

function browserVisibility(): VisibilitySource | undefined {
  if (typeof document === "undefined") return undefined;
  return {
    isHidden: () => document.visibilityState === "hidden",
    onChange(cb) {
      document.addEventListener("visibilitychange", cb);
      return () => document.removeEventListener("visibilitychange", cb);
    },
  };
}

function browserOnline() {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") return undefined;
  return {
    onChange(cb: () => void) {
      window.addEventListener("online", cb);
      return () => window.removeEventListener("online", cb);
    },
  };
}

/** 终端文本 ⇄ 字节 */
const enc = new TextEncoder();
export const encodeTerminalText = (s: string) => enc.encode(s);
export function terminalDecoder() {
  const dec = new TextDecoder();
  return (bytes: Uint8Array) => dec.decode(bytes, { stream: true });
}

export { encodeBinaryFrame, decodeBinaryFrame };
